import { useRef, useState, useCallback, useEffect } from 'react';
import cls from 'clsx';
import { HEADER_HEIGHT } from '~/constants';
import styles from './sheet.module.css';

export const SHEET_BOTTOM_HEIGHT = 24;
const SHEET_TITLE_PEEK_HEIGHT = 68;
const SEARCH_BAR_HEIGHT = 70;

// A header action button: the caller owns the icon, handler and disabled
// state; Sheet owns the rendering and styling.
export interface SheetAction {
  icon: string;
  onClick?: () => void;
  disabled?: boolean;
}

interface SheetProps {
  children?: React.ReactNode;
  title?: string;
  actions?: SheetAction[];
  height?: number;
  position?: PositionType;
  snapPositions: PositionType[];
  contentKey?: string;
  className?: string;
  onHeightChange?: (height: number) => void;
  onPositionChange?: (position: PositionType) => void;
  onDragEnd?: () => void;
}

export const Position = {
  None: 0,
  Bottom: 0.1,
  TitlePeek: 0.2,
  Middle: 0.5,
  MiddleHigh: 0.8,
  High: 0.9
} as const;

export type PositionType = (typeof Position)[keyof typeof Position];

export const SheetContent = {
  BookingSummary: 'bookingSummary',
  PoiSummary: 'poiSummary',
  PoiList: 'poiList'
} as const;

export type SheetContent = (typeof SheetContent)[keyof typeof SheetContent];

export default function Sheet({
  children,
  title,
  actions,
  height = SHEET_BOTTOM_HEIGHT,
  position = Position.Bottom,
  snapPositions,
  contentKey,
  className,
  onHeightChange,
  onPositionChange,
  onDragEnd
}: SheetProps) {
  const [isDragging, setIsDragging] = useState(false);

  const sheetRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef({ x: 0, y: 0, height: 0, currentHeight: height });
  const activePointerIdRef = useRef<number | null>(null);
  const dragEngagedRef = useRef(false);
  const fromDragZoneRef = useRef(false);

  const getHeightForPosition = useCallback((pos: PositionType) => {
    if (pos === Position.None) return 0;
    if (pos === Position.Bottom) return SHEET_BOTTOM_HEIGHT;
    if (pos === Position.TitlePeek) return SHEET_TITLE_PEEK_HEIGHT;
    if (pos === Position.Middle) return window.innerHeight * 0.5;
    if (pos === Position.MiddleHigh)
      return window.innerHeight - SEARCH_BAR_HEIGHT;
    return window.innerHeight - HEADER_HEIGHT;
  }, []);

  const getActiveSnaps = useCallback(() => {
    return snapPositions
      .map(pos => ({ position: pos, height: getHeightForPosition(pos) }))
      .sort((a, b) => a.height - b.height);
  }, [snapPositions, getHeightForPosition]);

  useEffect(() => {
    onHeightChange?.(getHeightForPosition(position));
  }, [position, onHeightChange, getHeightForPosition]);

  // Sheets stay mounted while closed (so they can animate out), which would
  // otherwise reopen them at the previous scroll offset — reset to the top on
  // every open.
  const prevPositionRef = useRef(position);
  useEffect(() => {
    const prev = prevPositionRef.current;
    prevPositionRef.current = position;
    if (prev === Position.None && position !== Position.None) {
      contentRef.current?.scrollTo({ top: 0 });
    }
  }, [position]);

  // Also reset when the open sheet's content is swapped for another item
  // (e.g. tapping a different POI while its sheet is already up).
  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0 });
  }, [contentKey]);

  // Block native scrolling while a content gesture drags the sheet.
  // Needs a non-passive listener, so it can't be a React prop.
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;
    const handleTouchMove = (e: TouchEvent) => {
      if (dragEngagedRef.current) e.preventDefault();
    };
    content.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => content.removeEventListener('touchmove', handleTouchMove);
  }, []);

  // Snap to the position nearest to where the drag ended, so a long drag
  // can skip intermediate positions. A short drag past the threshold still
  // moves one step in the drag direction. Returns true if the position
  // actually changed.
  const snapByDirection = useCallback(
    (startHeight: number, currentHeight: number): boolean => {
      const snaps = getActiveSnaps();
      if (snaps.length === 0) return false;

      const nearestTo = (height: number) =>
        snaps.reduce((prev, curr) =>
          Math.abs(curr.height - height) < Math.abs(prev.height - height)
            ? curr
            : prev
        );

      const startSnap = nearestTo(startHeight);
      const delta = currentHeight - startSnap.height;
      const DIRECTION_THRESHOLD = 8;

      let target = nearestTo(currentHeight);
      if (target.position === startSnap.position) {
        if (delta > DIRECTION_THRESHOLD) {
          const above = snaps.filter(s => s.height > startSnap.height);
          if (above.length > 0) target = above[0];
        } else if (delta < -DIRECTION_THRESHOLD) {
          const below = snaps.filter(s => s.height < startSnap.height);
          if (below.length > 0) target = below[below.length - 1];
        }
      }

      onHeightChange?.(target.height);
      onPositionChange?.(target.position);
      return target.position !== startSnap.position;
    },
    [getActiveSnaps, onHeightChange, onPositionChange]
  );

  // Drag start. A gesture on the handle drags the sheet immediately; a
  // gesture on the content is only tracked, and takes over as a sheet drag
  // later if the content is at its top and the pointer moves downward.
  // Content marked with data-sheet-drag-zone (sticky headers) also only
  // tracks — so taps on its buttons still click — but takes over in both
  // directions regardless of scroll position.
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const fromHandle = handleRef.current?.contains(e.target as Node);
    const fromContent = contentRef.current?.contains(e.target as Node);
    if (!fromHandle && !fromContent) return;

    fromDragZoneRef.current = !!(e.target as Element).closest(
      '[data-sheet-drag-zone]'
    );
    activePointerIdRef.current = e.pointerId;
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      height,
      currentHeight: height
    };
    dragEngagedRef.current = false;

    if (fromHandle) {
      e.preventDefault();
      dragEngagedRef.current = true;
      setIsDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (activePointerIdRef.current !== e.pointerId) return;

    if (!dragEngagedRef.current) {
      const content = contentRef.current;
      const movedDown = e.clientY - dragStartRef.current.y;
      const movedX = Math.abs(e.clientX - dragStartRef.current.x);
      const SLOP = 8;

      // A mostly horizontal gesture (e.g. a carousel swipe) is never a
      // sheet drag — let the browser own it.
      const horizontalPan = movedX > SLOP && movedX > Math.abs(movedDown);
      if (horizontalPan) {
        activePointerIdRef.current = null;
        return;
      }

      if (fromDragZoneRef.current) {
        // Drag zones drag the sheet in both directions, like the handle.
        if (Math.abs(movedDown) <= SLOP) return;
      } else {
        // The content is scrolling vertically — let the browser own it.
        // A content gesture only becomes a sheet drag when the content is
        // at its top and the pointer moves downward.
        if (!content || content.scrollTop > 0 || movedDown < -SLOP) {
          activePointerIdRef.current = null;
          return;
        }
        if (movedDown <= SLOP) return;
      }

      // Take over as a sheet drag. Rebase the start so the sheet doesn't
      // jump.
      dragStartRef.current.y = e.clientY;
      dragEngagedRef.current = true;
      setIsDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }

    const snaps = getActiveSnaps();
    if (snaps.length === 0) return;
    const minHeight = snaps[0].height;
    const maxHeight = snaps[snaps.length - 1].height;
    const deltaY = dragStartRef.current.y - e.clientY;
    const newHeight = Math.min(
      maxHeight,
      Math.max(minHeight, dragStartRef.current.height + deltaY)
    );
    dragStartRef.current.currentHeight = newHeight;
    onHeightChange?.(newHeight);
  };

  const finishDrag = useCallback(
    (pointerId: number) => {
      if (activePointerIdRef.current !== pointerId) return;
      activePointerIdRef.current = null;
      const wasEngaged = dragEngagedRef.current;
      dragEngagedRef.current = false;
      setIsDragging(false);
      if (!wasEngaged) return;
      const positionChanged = snapByDirection(
        dragStartRef.current.height,
        dragStartRef.current.currentHeight
      );
      if (positionChanged) onDragEnd?.();
    },
    [snapByDirection, onDragEnd]
  );

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    finishDrag(e.pointerId);
  };

  const handlePointerCancel = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    finishDrag(e.pointerId);
  };

  return (
    <div
      ref={sheetRef}
      className={cls(styles.sheet, isDragging && styles.dragging, className)}
      style={{ height: `${height}px` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
    >
      <div ref={handleRef} className={styles.handle} />
      <div ref={contentRef} className={styles.content}>
        {(title || (actions && actions.length > 0)) && (
          <div className={styles.header} data-sheet-drag-zone>
            {title && <h2 className={styles.headerTitle}>{title}</h2>}
            {actions && actions.length > 0 && (
              <div className={styles.headerActions}>
                {actions.map((action, i) => (
                  <button
                    key={i}
                    type="button"
                    className={styles.headerButton}
                    onClick={action.onClick}
                    disabled={action.disabled}
                  >
                    <img className={styles.headerIcon} src={action.icon} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}
